import { AfterViewInit, ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrencyPipe } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { ContactComponent } from '../../shared/components/contact/contact.component';
import { Themes } from '../../../themes';
import { BannerComponent } from '../../shared/components/banner/banner.component';
import { SliderComponent } from '../../shared/components/slider/slider.component';
import { NumberInputDirective } from '../../shared/directives/number-input-directive';
import { LandingpageConfiguration } from '../../shared/models/landingpage-configuration.model';
import { ManualPaymentComponent } from './manual-payment/manual-payment.component';
import { PaymentMethodSelectionComponent } from './payment-method-selection/payment-method-selection.component';
import {
  getSelectablePaymentTypes,
  hasManualPayment,
  SelectablePaymentType,
} from 'app/shared/models/payment-configuration.model';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';
import { InfoboxComponent } from 'app/shared/components/infobox/infobox.component';
import { ExpandableComponent } from 'app/shared/components/expandable/expandable.component';
import { ButtonComponent } from 'app/shared/components/button/button.component';
import { SepaDataService } from 'app/shared/services/sepa-data.service';
import { HandlebarsPipe } from 'app/shared/pipes/templating/handlebars.pipe';

@Component({
  selector: 'ez-overview',
  templateUrl: './overview.component.html',
  styleUrl: './overview.component.scss',
  imports: [
    MainLayoutComponent,
    ReactiveFormsModule,
    BannerComponent,
    InfoboxComponent,
    SliderComponent,
    ExpandableComponent,
    CurrencyPipe,
    PaymentMethodSelectionComponent,
    ButtonComponent,
    ManualPaymentComponent,
    ContactComponent,
    NumberInputDirective,
    HandlebarsPipe,
  ],
  providers: [CurrencyPipe],
})
export class OverviewComponent implements OnInit, AfterViewInit {
  private formBuilder = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sepaDataService = inject<SepaDataService>(SepaDataService);
  private cdr = inject(ChangeDetectorRef);

  @ViewChild(SliderComponent) slider!: SliderComponent;
  @ViewChild(ExpandableComponent) expandable: ExpandableComponent;

  protected readonly Themes = Themes;
  hasManualPayment = hasManualPayment;

  form: FormGroup;
  landingpageConfig: LandingpageConfiguration;
  paymentMethods: SelectablePaymentType[];
  isCustomAmountOpen = false;

  ngOnInit(): void {
    this.route.data.subscribe({
      next: (data) => {
        this.landingpageConfig = data['landingPageConfiguration'];
        this.paymentMethods = getSelectablePaymentTypes(
          this.landingpageConfig.paymentConfiguration.allowedPaymentTypes,
        );
      },
    });

    this.form = this.formBuilder.group({
      sliderValue: [],
      customAmount: [],
      paymentMethod: [
        'SEPA',
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ],
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  get customAmountControl(): AbstractControl {
    return this.form.get('customAmount')!;
  }

  get selectedAmount(): number {
    if (this.isCustomAmountOpen) {
      return this.customAmountControl.value ?? 0;
    } else {
      return this.slider?.currentValue()?.additionalPaymentAmount ?? 0;
    }
  }

  detailsOpened(isOpened: boolean): void {
    this.isCustomAmountOpen = isOpened;
    this.customAmountControl.updateValueAndValidity();

    if (isOpened) {
      this.slider.setDisabled();
    } else {
      this.slider.setEnabled(new ToggleEvent('detailsOpened'));
    }
  }

  onSliderEnabled() {
    if (this.isCustomAmountOpen) {
      this.expandable.close();
    }
  }

  onSubmit() {
    this.sepaDataService.setPaymentAmount(this.selectedAmount);
    this.router.navigate(['sepa']);
  }

  hasSelectablePaymentMethod(): boolean {
    return this.paymentMethods.length > 0;
  }
}
